// 3. slice.ts
import { createSlice } from "@reduxjs/toolkit";
import {
  AddNotesApiResponse,
  AddTimecardApiResponse,
  AdjustTimeCardResponse,
  GetAuditApiResponse,
  GetNotesApiResponse,
  GroupedTimeCardApiResponse,
  SummaryData,
  TimecardRecordDayDetails,
  TimecardType,
  TimesheetState
} from "../../../types/state/timecards";
import {
  addNotesToTimecardRecord,
  addTimecardRecord,
  adjustTimeCardApi,
  getAuditLogsApi,
  getEmployeeTimeCard,
  getEmployeeTimeCardGrouped,
  getEmployeeTimeCardRecordDetail,
  getEmployeeTimeCardSummary,
  getNotesToTimecardRecord,
  getThisWeekTimeCardSummary,
  getTodayTimeCardSummary,
} from "./api";
import { FULFILLED, PENDING, REJECTED } from "../../constants";

const initialState: TimesheetState = {
  timecards: [],
  addTimecardData: null,
  groupedTimesheets: null,
  timecardRecordDayDetailsData: null,
  summary: {
    total_hours: 0,
    total_paid_timeoff: 0,
    total_unpaid_timeoff: 0,
  },
  todaySummary: {
    total_hours: 0,
    total_paid_timeoff: 0,
    total_unpaid_timeoff: 0,
  },
  thisWeekSummary: {
    total_hours: 0,
    total_paid_timeoff: 0,
    total_unpaid_timeoff: 0,
  },
  getAuditLogsData: null,
  adjustResponse: null,
  timecardNotesData: null,
  addToTimecardNotesData: null,
  requestStatus: FULFILLED,
  loading: false,
};

export const EmployeeTimeCardSlice = createSlice({
  name: "employeeTimecard",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getEmployeeTimeCard.pending, (state) => {
        state.timecards = [];
        state.requestStatus = PENDING;
      })
      .addCase(getEmployeeTimeCard.fulfilled, (state, { payload }) => {
        state.timecards = payload || [];
        state.requestStatus = FULFILLED;
      })
      .addCase(getEmployeeTimeCard.rejected, (state) => {
        state.timecards = [];
        state.requestStatus = REJECTED;
      });
    builder
      .addCase(getEmployeeTimeCardSummary.pending, (state) => {
        state.summary = {
          total_hours: 0,
          total_paid_timeoff: 0,
          total_unpaid_timeoff: 0,
        };
        state.requestStatus = PENDING;
      })
      .addCase(getEmployeeTimeCardSummary.fulfilled, (state, { payload }) => {
        state.summary = payload?.summary || {
          total_hours: 0,
          total_paid_timeoff: 0,
          total_unpaid_timeoff: 0,
        };
        state.requestStatus = FULFILLED;
      })
      .addCase(getEmployeeTimeCardSummary.rejected, (state) => {
        state.summary = {
          total_hours: 0,
          total_paid_timeoff: 0,
          total_unpaid_timeoff: 0,
        };
        state.requestStatus = REJECTED;
      });
    builder
      .addCase(getEmployeeTimeCardGrouped.pending, (state) => {
        state.groupedTimesheets = null;
        state.requestStatus = PENDING;
      })
      .addCase(getEmployeeTimeCardGrouped.fulfilled, (state, { payload }) => {
        state.groupedTimesheets = payload || null;
        state.requestStatus = FULFILLED;
      })
      .addCase(getEmployeeTimeCardGrouped.rejected, (state) => {
        state.groupedTimesheets = null;
        state.requestStatus = REJECTED;
      });
    // Handling Today's Summary
    builder.addCase(getTodayTimeCardSummary.pending, (state) => {
      state.todaySummary = {
        total_hours: 0,
        total_paid_timeoff: 0,
        total_unpaid_timeoff: 0,
      };
      state.requestStatus = PENDING;
    });
    builder.addCase(getTodayTimeCardSummary.fulfilled, (state, { payload }) => {
      state.todaySummary = payload?.summary || {
        total_hours: 0,
        total_paid_timeoff: 0,
        total_unpaid_timeoff: 0,
      };
      state.requestStatus = FULFILLED;
    });
    builder.addCase(getTodayTimeCardSummary.rejected, (state, action) => {
      state.todaySummary = {
        total_hours: 0,
        total_paid_timeoff: 0,
        total_unpaid_timeoff: 0,
      };
      state.requestStatus = REJECTED;
    });

    // Handling This Week's Summary (no lastFetched)
    builder.addCase(getThisWeekTimeCardSummary.pending, (state) => {
      state.thisWeekSummary = {
        total_hours: 0,
        total_paid_timeoff: 0,
        total_unpaid_timeoff: 0,
      };
      state.requestStatus = PENDING;
      state.loading = true;
    });
    
    builder.addCase(getThisWeekTimeCardSummary.fulfilled, (state, { payload }) => {
      state.thisWeekSummary = payload?.summary || {
        total_hours: 0,
        total_paid_timeoff: 0,
        total_unpaid_timeoff: 0,
      };
      state.requestStatus = FULFILLED;
      state.loading = false;
    });
    
    builder.addCase(getThisWeekTimeCardSummary.rejected, (state) => {
      state.thisWeekSummary = {
        total_hours: 0,
        total_paid_timeoff: 0,
        total_unpaid_timeoff: 0,
      };
      state.requestStatus = REJECTED;
      state.loading = false;
    });
    builder
      .addCase(getEmployeeTimeCardRecordDetail.pending, (state) => {
        state.timecardRecordDayDetailsData = null;
        state.requestStatus = PENDING;
      })
      .addCase(
        getEmployeeTimeCardRecordDetail.fulfilled,
        (state, { payload }) => {
          state.timecardRecordDayDetailsData = payload || null;
          state.requestStatus = FULFILLED;
        }
      )
      .addCase(getEmployeeTimeCardRecordDetail.rejected, (state) => {
        state.timecardRecordDayDetailsData = null;
        state.requestStatus = REJECTED;
      });
    builder
      .addCase(getAuditLogsApi.pending, (state) => {
        state.getAuditLogsData = null;
        state.requestStatus = PENDING;
      })
      .addCase(getAuditLogsApi.fulfilled, (state, { payload }) => {
        state.getAuditLogsData = payload || null;
        state.requestStatus = FULFILLED;
      })
      .addCase(getAuditLogsApi.rejected, (state) => {
        state.getAuditLogsData = null;
        state.requestStatus = REJECTED;
      });
    builder
      .addCase(adjustTimeCardApi.pending, (state) => {
        state.requestStatus = PENDING;
        state.adjustResponse = null;
      })
      .addCase(adjustTimeCardApi.fulfilled, (state, { payload }) => {
        state.requestStatus = FULFILLED;
        state.adjustResponse = payload;
      })
      .addCase(adjustTimeCardApi.rejected, (state, action) => {
        state.requestStatus = REJECTED;
        state.adjustResponse = null;
      });
    builder
      .addCase(getNotesToTimecardRecord.pending, (state) => {
        // state.timecardNotesData = null;
        state.requestStatus = PENDING;
      })
      .addCase(getNotesToTimecardRecord.fulfilled, (state, { payload }) => {
        state.timecardNotesData = payload || null;
        state.requestStatus = FULFILLED;
      })
      .addCase(getNotesToTimecardRecord.rejected, (state) => {
        state.timecardNotesData = null;
        state.requestStatus = REJECTED;
      });

    // Handle Add Notes API
    builder
      .addCase(addNotesToTimecardRecord.pending, (state) => {
        state.addToTimecardNotesData = null;
        state.requestStatus = PENDING;
      })
      .addCase(addNotesToTimecardRecord.fulfilled, (state, { payload }) => {
        state.addToTimecardNotesData = payload || null;
        state.requestStatus = FULFILLED;
      })
      .addCase(addNotesToTimecardRecord.rejected, (state) => {
        state.addToTimecardNotesData = null;
        state.requestStatus = REJECTED;
      });

    builder
      .addCase(addTimecardRecord.pending, (state) => {
        state.addTimecardData = null;
        state.requestStatus = PENDING;
      })
      .addCase(addTimecardRecord.fulfilled, (state, { payload }) => {
        state.addTimecardData = payload || null;
        state.requestStatus = FULFILLED;
      })
      .addCase(addTimecardRecord.rejected, (state) => {
        state.addTimecardData = null;
        state.requestStatus = REJECTED;
      });
  },
});

export default EmployeeTimeCardSlice.reducer;
